import { useEffect, useMemo, useState } from "react";
import { Upload } from "lucide-react";

// Option selector shared by QuickView and the product page.
// Handles SELECT/RADIO-style choice options; file-upload (ID verification)
// options are completed inside the secure Ecwid checkout.
export function OptionSelector({ product, onChange }) {
  const choiceOptions = useMemo(
    () => (product.options || []).filter((o) => o.choices && o.choices.length > 0),
    [product]
  );
  const uploadOptions = useMemo(
    () => (product.options || []).filter((o) => o.type === "UPLOADFILES" || o.type === "FILES"),
    [product]
  );

  const [selected, setSelected] = useState(() => {
    const init = {};
    choiceOptions.forEach((o) => {
      const def = o.choices.find((c) => c.default) || o.choices[0];
      init[o.name] = def.text;
    });
    return init;
  });

  const priceDelta = choiceOptions.reduce((sum, o) => {
    const c = o.choices.find((ch) => ch.text === selected[o.name]);
    return sum + (c?.priceModifier || 0);
  }, 0);

  // Emit the default selection on mount so parents always have valid options.
  useEffect(() => {
    onChange?.({
      options: selected,
      priceDelta,
      display: choiceOptions.map((o) => `${o.name}: ${selected[o.name]}`),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (name, value) => {
    const next = { ...selected, [name]: value };
    setSelected(next);
    const delta = choiceOptions.reduce((sum, o) => {
      const c = o.choices.find((ch) => ch.text === next[o.name]);
      return sum + (c?.priceModifier || 0);
    }, 0);
    const display = choiceOptions.map((o) => `${o.name}: ${next[o.name]}`);
    onChange?.({ options: next, priceDelta: delta, display });
  };

  return (
    <div className="space-y-5" data-testid="option-selector">
      {choiceOptions.map((opt) => (
        <fieldset key={opt.name}>
          <legend className="mb-2.5 text-xs font-bold uppercase tracking-[0.18em] text-brand-ink/60">
            {opt.name} {opt.required && <span className="text-brand-golddeep">*</span>}
          </legend>
          <div className="flex flex-wrap gap-2">
            {opt.choices.map((choice) => {
              const active = selected[opt.name] === choice.text;
              return (
                <button
                  key={choice.text}
                  type="button"
                  data-testid={`option-${opt.name.replace(/\W+/g, "-").toLowerCase()}-${choice.text.replace(/\W+/g, "-").toLowerCase()}`}
                  aria-pressed={active}
                  onClick={() => select(opt.name, choice.text)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                    active
                      ? "border-brand-pine bg-brand-pine text-brand-cream"
                      : "border-brand-line bg-white text-brand-ink/70 hover:border-brand-pine/50"
                  }`}
                >
                  {choice.text}
                  {choice.priceModifier ? (
                    <span className="ml-1.5 text-xs opacity-70">+${choice.priceModifier}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}
      {uploadOptions.length > 0 && (
        <p
          data-testid="id-verification-note"
          className="flex items-start gap-2 rounded-xl border border-brand-gold/40 bg-brand-gold/10 px-4 py-3 text-xs leading-relaxed text-brand-golddeep"
        >
          <Upload className="mt-0.5 h-4 w-4 shrink-0" />
          Age verification required: you'll confirm your 21+ ID securely during checkout.
        </p>
      )}
      <input type="hidden" value={priceDelta} readOnly aria-hidden="true" />
    </div>
  );
}
